import React, { useEffect, useState } from "react";
import OrgListing from "./OrgListing";
import useOrganization from "store/organization/service-hook";
import useFilters, { FILTER_TYPES } from "helpers/hooks/useFilters";
import useRedirect from "helpers/hooks/useRedirect";
import { COMPONENT_TYPES, ROLES } from "helpers/utils/constants";
import { useSelector } from "react-redux";
import { REDUCER_TYPES } from "store";
import { isSuperAdmin } from "helpers/utils/utilities";
import ManageWrapper from "components/UI/organisms/ManageWrapper";

const ManageOrganizations = () => {
  const [gridType, setGridType] = useState(1);
  const { getOrganizationList, deleteOrganization } = useOrganization();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("creation_date");
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const { selected } = useFilters();
  const { navigateCreateOrg } = useRedirect();
  const { user } = useSelector((state) => state[REDUCER_TYPES.AUTH]);

  // FETCH ORGANIZATION LIST ON FILTER, SORT, PAGE, OR SEARCH CHANGE
  useEffect(() => {
    const abortController = new AbortController();
    fetchOrganizations(abortController);
    return () => abortController.abort();
  }, [selected, sort, page, search]);

  // FETCH ORGANIZATIONS BASED ON CURRENT FILTERS
  const fetchOrganizations = async (abortController) => {
    setLoading(true);
    const filters = {
      ...selected,
      sort_by: sort,
      search,
      page,
      owner: selected.owner || "all",
    };

    // NORMALIZE ORGANIZATION TYPE TO CATEGORY FIELD
    if (selected?.organization_type)
      filters.category = selected.organization_type;
    delete filters?.organization_type;

    const [list, count] = await getOrganizationList(filters);

    // UPDATE STATE IF NOT ABORTED
    if (!abortController || !abortController.signal.aborted) {
      setOrganizations(list || []);
      setPageCount(count);
      setLoading(false);
    }
  };

  // DELETE ORGANIZATION AND REMOVE FROM LOCAL STATE
  const handleDelete = async (slug) => {
    await deleteOrganization(slug);
    setOrganizations((prev) => prev.filter((d) => d.slug !== slug));
  };

  return (
    <ManageWrapper
      gridType={gridType}
      search={search}
      setSearch={setSearch}
      setGridType={setGridType}
      sort={sort}
      setSort={setSort}
      createActionTitle="Create Organization"
      navigateCreate={navigateCreateOrg}
      component={COMPONENT_TYPES.ORGANIZATION}
      filters={[
        FILTER_TYPES.AUTHOR,
        FILTER_TYPES.PLAN,
        FILTER_TYPES.ORGANIZATION_TYPE,
        FILTER_TYPES.VERIFICATION_STATUS,
      ].filter(Boolean)}
      loading={loading}
      noAction={
        !isSuperAdmin(user?.roles) &&
        user?.organization_details?.roles !== ROLES.ORGANIZATION_OWNER
      }
      pageTitle="Manage Organizations"
    >
      <OrgListing
        gridType={gridType}
        list={organizations}
        handleDelete={handleDelete}
        loading={loading}
        page={page}
        setPage={setPage}
        pageCount={pageCount}
        manage
        fetchOrganizations={fetchOrganizations}
      />
    </ManageWrapper>
  );
};

export default ManageOrganizations;
